// pages/Packages.jsx
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LanguageContext } from '../context/LanguageContext';
import whatsappBotImage from "../assets/robot.png";

// ==================== Animations ====================
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;
const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(30, 111, 142, 0.7); }
  70% { box-shadow: 0 0 0 15px rgba(30, 111, 142, 0); }
  100% { box-shadow: 0 0 0 0 rgba(30, 111, 142, 0); }
`;
const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;
const robotWave = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(10deg); }
  75% { transform: rotate(-10deg); }
  100% { transform: rotate(0deg); }
`;

// ==================== Styled Components ====================
const PageContainer = styled.div`
  font-family: 'Cairo', 'Segoe UI', 'Tahoma', sans-serif;
  background: #d8e4e7d7;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(79, 163, 199, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
  @media (max-width: 800px) { padding: 0 20px; }
`;
const HeroSection = styled.div`
  text-align: center;
  padding: 70px 0 50px;
  animation: ${fadeInUp} 0.8s ease-out;
`;
const Badge = styled.div`
  display: inline-block;
  background: rgba(30, 111, 142, 0.15);
  backdrop-filter: blur(10px);
  padding: 8px 24px;
  border-radius: 60px;
  font-weight: 700;
  font-size: 0.9rem;
  color: #1E6F8E;
  margin-bottom: 24px;
  border: 1px solid rgba(30, 111, 142, 0.3);
  letter-spacing: 0.5px;
  &:hover { background: rgba(30, 111, 142, 0.25); transform: scale(1.02); transition: all 0.3s ease; }
`;
const Title = styled.h1`
  font-size: 3.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1E6F8E, #0F4C5C, #1E6F8E);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 20px;
  @media (max-width: 800px) { font-size: 2.3rem; }
`;
const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #2C5A6E;
  max-width: 800px;
  margin: 0 auto 20px;
  font-weight: 500;
  line-height: 1.7;
  text-align: ${props => props.dir === 'rtl' ? 'right' : 'left'};
`;
const BotHighlight = styled.span`
  background: linear-gradient(135deg, #1E6F8E, #0F4C5C);
  padding: 4px 12px;
  border-radius: 40px;
  font-weight: 700;
  display: inline-block;
  margin-top: 10px;
  font-size: 0.9rem;
  color: #FFFFFF;
`;
const PackagesSection = styled.section`
  padding: 50px 0 80px;
`;
const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 20px;
  color: #1E6F8E;
  position: relative;
  &:after {
    content: "";
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #1E6F8E, #4FA3C7, #1E6F8E);
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 4px;
  }
`;
const SectionDescription = styled.p`
  text-align: center;
  max-width: 750px;
  margin: 25px auto 0;
  color: #2C5A6E;
  font-size: 1.1rem;
`;
const PackagesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 70px;
`;
const PackageCard = styled.div`
  background: #FFFFFF;
  border-radius: 48px;
  padding: 32px 28px;
  flex: 1;
  min-width: 280px;
  max-width: 340px;
  transition: all 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  box-shadow: 0 25px 45px -18px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(30, 111, 142, 0.2);
  position: relative;
  animation: ${scaleIn} 0.5s ease-out backwards;
  animation-delay: ${props => props.delay || '0s'};
  &:hover {
    transform: translateY(-16px) scale(1.02);
    box-shadow: 0 40px 60px -20px rgba(0, 0, 0, 0.25);
    border-color: #4FA3C7;
  }
`;
const PopularRibbon = styled.div`
  position: absolute;
  top: -14px;
  ${props => props.dir === 'rtl' ? 'left: 24px;' : 'right: 24px;'}
  background: linear-gradient(135deg, #1E6F8E, #0F4C5C);
  color: #FFFFFF;
  font-weight: 800;
  font-size: 0.75rem;
  padding: 6px 16px;
  border-radius: 60px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 6px;
  &::before { content: '🤖'; font-size: 0.85rem; }
`;
const PackageIcon = styled.div` font-size: 2.5rem; margin-bottom: 16px; text-align: center; `;
const PackageName = styled.h3`
  font-size: 1.7rem;
  font-weight: 800;
  margin-bottom: 10px;
  background: linear-gradient(120deg, #1E6F8E, #4FA3C7);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-align: center;
`;
const PackageDesc = styled.div`
  font-size: 0.85rem;
  color: #666666;
  margin-bottom: 20px;
  border-bottom: 2px solid #F2F4F7;
  padding-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
`;
const Price = styled.div`
  font-size: 2.4rem;
  font-weight: 800;
  color: #1E6F8E;
  margin: 20px 0 8px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
`;
const PriceSpan = styled.span` font-size: 1rem; font-weight: 500; color: #666666; `;
const PriceNote = styled.div`
  font-size: 0.7rem;
  color: #1E6F8E;
  background: #F2F4F7;
  padding: 6px 12px;
  border-radius: 40px;
  display: inline-block;
  width: auto;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
`;
const FeaturesList = styled.ul` list-style: none; margin: 24px 0 28px; padding: 0; `;
const FeatureItem = styled.li`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  font-size: 0.85rem;
  color: #333333;
  transition: transform 0.2s ease;
  &:hover { transform: translateX(5px); }
  span {
    width: 22px; height: 22px; display: inline-flex; align-items: center;
    justify-content: center; background: linear-gradient(135deg, #1E6F8E, #4FA3C7);
    border-radius: 50%; color: white; font-size: 0.7rem; flex-shrink: 0;
  }
`;
const SubscribeButton = styled.button`
  display: inline-block;
  width: 100%;
  text-align: center;
  background: #F2F4F7;
  padding: 12px 0;
  border-radius: 60px;
  font-weight: 800;
  font-size: 0.95rem;
  color: #1E6F8E;
  transition: all 0.3s ease;
  text-decoration: none;
  border: 1px solid #E0E6ED;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(30, 111, 142, 0.15), transparent);
    transition: left 0.5s ease;
  }
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1E6F8E, #0F4C5C);
    color: white;
    border-color: #1E6F8E;
    transform: translateY(-2px);
  }
  &:hover:not(:disabled)::before { left: 100%; }
`;
const ExtraFeaturesBox = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,255,255,0.95));
  border-radius: 48px;
  padding: 32px 36px;
  max-width: 900px;
  width: 100%;
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-top: 60px;
  transition: all 0.4s ease;
  border: 1px solid rgba(30, 111, 142, 0.2);
  &:hover { transform: translateY(-8px); box-shadow: 0 30px 50px -16px rgba(0, 0, 0, 0.2); }
`;
const ExtraFeaturesTitle = styled.h3`
  margin: 12px 0 8px;
  font-size: 1.7rem;
  background: linear-gradient(135deg, #1E6F8E, #4FA3C7);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 800;
`;
const FeaturesTags = styled.div` display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin-top: 20px; `;
const FeatureTag = styled.span`
  background: #F2F4F7;
  border-radius: 60px;
  padding: 8px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1E6F8E;
  transition: all 0.3s ease;
  cursor: default;
  &:hover { background: linear-gradient(135deg, #1E6F8E, #4FA3C7); color: white; transform: translateY(-3px); }
`;
const CTAContainer = styled.div`
  background: linear-gradient(135deg, #1E6F8E, #0F4C5C, #1E6F8E);
  background-size: 200% 200%;
  border-radius: 56px;
  padding: 56px 40px;
  text-align: center;
  margin: 50px 0 60px;
  color: white;
  box-shadow: 0 25px 45px -12px rgba(0, 0, 0, 0.2);
  animation: ${shimmer} 8s ease infinite;
  &:hover { transform: scale(1.01); transition: transform 0.4s ease; }
`;
const CTAText = styled.p`
  font-size: 1.1rem;
  opacity: 0.95;
  margin-bottom: 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;
const CTAButton = styled(Link)`
  background: #FFFFFF;
  color: #1E6F8E;
  padding: 14px 42px;
  border-radius: 60px;
  font-weight: 800;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 1.05rem;
  &:hover { background: #F2F4F7; transform: translateY(-3px) scale(1.02); box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
`;
const FooterContainer = styled.footer`
  text-align: center;
  padding: 40px 0 50px;
  border-top: 1px solid rgba(30, 111, 142, 0.2);
  color: #2C5A6E;
  font-size: 0.85rem;
  margin-top: 20px;
`;
const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;
const FooterLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.3s ease;
  &:hover { transform: translateY(-3px); color: #1E6F8E; }
`;
const RobotButton = styled.a`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 999;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  animation: ${pulseRing} 2s infinite;
  &:hover { transform: scale(1.1) rotate(5deg); box-shadow: 0 15px 35px rgba(37, 211, 102, 0.5); }
`;
const RobotIcon = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  animation: ${robotWave} 3s ease-in-out infinite;
  transform-origin: center;
`;
const RobotTooltip = styled.span`
  position: absolute;
  bottom: 85px;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  color: #25D366;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  font-family: 'Cairo', sans-serif;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateX(15px);
  transition: all 0.3s ease;
  pointer-events: none;
  letter-spacing: 0.3px;
  border: 1px solid rgba(37, 211, 102, 0.5);
  z-index: 1000;
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 28px;
    border-width: 8px 8px 0 8px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.9) transparent transparent transparent;
  }
  ${RobotButton}:hover & { opacity: 1; transform: translateX(0); }
`;
const LangSwitcher = styled.button`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 800;
  background: #1E6F8E;
  color: white;
  border: none;
  border-radius: 40px;
  padding: 8px 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  &:hover { background: #0F4C5C; transform: scale(1.05); }
`;

// ==================== Translations ====================
const translations = {
  badge: { ar: ' رواد التكنولوجيا في القطاع الطبي', en: ' Technology Pioneers in Healthcare' },
  title: { ar: 'حلول والتحول الرقمي', en: 'Digital Solutions & Transformation' },
  subtitle: { ar: `نحن شركة متخصصة في تقديم حلول رقمية متكاملة للقطاع الطبي...`, en: `We are a company specialized in providing integrated digital solutions for the healthcare sector...` },
  botHighlight: { ar: ' نقدم تجربة رائعة في المجال الطبي | دعم فوري 24/7', en: ' Excellent experience in healthcare | 24/7 Instant Support' },
  packagesTitle: { ar: 'باقات الذكية للقطاع الطبي', en: 'Smart Packages for Healthcare' },
  packagesDesc: { ar: 'اختر البوت المناسب لعيادتك – أنظمة محادثة ذكية تعمل بالذكاء الاصطناعي', en: 'Choose the right bot for your clinic – AI-powered smart conversation systems' },
  extraFeaturesTitle: { ar: 'جميع باقات تشمل', en: 'All Packages Include' },
  extraTags: { ar: ['دعم فني 24/7', 'تحديثات مستمرة', 'تدريب على الاستخدام', 'تسليم خلال 3 أيام', 'تكامل مع أنظمة العيادات'], en: ['24/7 Technical Support', 'Continuous Updates', 'Usage Training', 'Delivery within 3 Days', 'Integration with Clinic Systems'] },
  ctaText: { ar: 'انضم إلى أكثر من 250 عيادة ومركز طبي...', en: 'Join over 250 clinics and medical centers...' },
  ctaButton: { ar: 'احصل على استشارة مجانية ←', en: 'Get a Free Consultation ←' },
  footerLinks: { ar: [' بوتات ذكية للقطاع الطبي', '🎧 دعم فني على مدار الساعة', '📊 تحليلات ذكية وتقارير', '🔒 أمان وخصوصية طبية'], en: [' Smart Bots for Healthcare', '🎧 24/7 Technical Support', '📊 Smart Analytics & Reports', '🔒 Medical Security & Privacy'] },
  footerText: { ar: '© 2025 جميع الحقوق محفوظة | شركة متخصصة في تطوير والحلول الرقمية', en: '© 2025 All Rights Reserved | A Company Specialized in Digital Solutions' },
  robotTooltip: { ar: 'تواصل مع بوت المساعدة 📲', en: 'Contact Support Bot 📲' },
  popularRibbon: { ar: 'الأكثر طلباً', en: 'Most Popular' },
  buttonText: { ar: 'اشترك الآن ←', en: 'Subscribe Now ←' },
  // No longer needed: demoAlert
};

// ==================== Static Package Data (6 Packages) ====================
const staticPackages = [
  { id: 1, name: 'الباقة الأساسية', description: 'بوت محادثة ذكي للمواعيد والاستفسارات', price: 299, maxProjects: 1 },
  { id: 2, name: 'باقة المحترفين', description: 'بوت متكامل + تحليلات وتقارير + تكامل مع نظام العيادة', price: 599, maxProjects: 3 },
  { id: 3, name: 'الباقة المتقدمة', description: 'حل متكامل مع دعم أولوية واستشارات مخصصة', price: 899, maxProjects: 5 },
  { id: 4, name: 'باقة المؤسسات', description: 'دعم متعدد الفروع وإدارة مركزية', price: 1299, maxProjects: 10 },
  { id: 5, name: 'الباقة الذهبية', description: 'كل ما سبق + استشارات حصرية وتخصيص كامل', price: 1999, maxProjects: 20 },
  { id: 6, name: 'باقة الشركات', description: 'حل متكامل للشركات مع فريق دعم مخصص', price: 2999, maxProjects: 'غير محدود' }
];

// ==================== Components ====================
const HeroSectionComponent = ({ lang }) => (
  <HeroSection>
    <Badge>{translations.badge[lang]}</Badge>
    <Title>{translations.title[lang]}</Title>
    <Subtitle dir={lang === 'ar' ? 'rtl' : 'ltr'}>{translations.subtitle[lang]}</Subtitle>
    <BotHighlight>{translations.botHighlight[lang]}</BotHighlight>
  </HeroSection>
);

const PackagesSectionComponent = ({ lang, packages }) => {
  const navigate = useNavigate();
  const getIcon = (index) => ['💬', '🤖', '📊', '🏥', '⭐', '💎'][index % 6];
  const getFeatures = (pkg) => {
    const features = [];
    if (pkg.maxProjects || pkg.max_projects) features.push(`📁 حتى ${pkg.maxProjects || pkg.max_projects} مشروع`);
    const description = lang === 'ar' ? (pkg.desc_ar || pkg.description) : (pkg.desc_en || pkg.description);
    if (description) features.push(description.substring(0, 60));
    features.push(lang === 'ar' ? '✅ دعم فني 24/7' : '✅ 24/7 Support');
    features.push(lang === 'ar' ? '🔄 تحديثات دورية' : '🔄 Regular Updates');
    features.push(lang === 'ar' ? '📊 لوحة تحكم متقدمة' : '📊 Config Dashboard');
    return features.slice(0, 5);
  };

  const getWhatsAppLink = (packageName) => {
    const phone = "201037334294";
    const message = `السلام عليكم، أرغب في الاشتراك في باقة: ${packageName}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  const handleSubscribe = async (pkg) => {
    const isLoggedInStr = localStorage.getItem("isLoggedIn") || sessionStorage.getItem("isLoggedIn");
    const isLoggedIn = isLoggedInStr === "true";
    if (!isLoggedIn) {
      alert(lang === 'ar' ? 'يجب تسجيل الدخول أولاً للإشتراك في الباقة' : 'You must sign in first to subscribe');
      navigate('/signin');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('userData') || sessionStorage.getItem('userData'));
    try {
      // Because axios requires absolute URI or base, we can use relative if same domain, but we are using absolute for React dev
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/buy_package.php' : '/api/buy_package.php';
      
      const response = await axios.post(apiUrl, {
        user_id: userData.id,
        package_name: pkg.name || pkg.name_ar || pkg.name_en,
        price: pkg.price.toString()
      });

      if (response.data.status === 'success') {
        alert(lang === 'ar' ? response.data.message : 'Package added to your account successfully!');
        navigate('/dashboard');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      alert(lang === 'ar' ? 'حدث خطأ أثناء الاشتراك' : 'An error occurred during subscription');
    }
  };

  return (
    <PackagesSection>
      <SectionTitle>{translations.packagesTitle[lang]}</SectionTitle>
      <SectionDescription>{translations.packagesDesc[lang]}</SectionDescription>
      <PackagesGrid>
        {packages.map((pkg, idx) => (
          <PackageCard key={pkg.id} delay={`${0.05 + idx * 0.05}s`}>
            {pkg.is_popular == 1 && <PopularRibbon dir={lang === 'ar' ? 'rtl' : 'ltr'}>{translations.popularRibbon[lang]}</PopularRibbon>}
            <PackageIcon>{getIcon(idx)}</PackageIcon>
            <PackageName>{lang === 'ar' ? (pkg.name_ar || pkg.name) : (pkg.name_en || pkg.name)}</PackageName>
            <PackageDesc><span>🤖</span> {(lang === 'ar' ? (pkg.desc_ar || pkg.description) : (pkg.desc_en || pkg.description))?.substring(0, 80) || 'باقة ذكية لعيادتك'}</PackageDesc>
            <FeaturesList>
              {getFeatures(pkg).map((feature, i) => (
                <FeatureItem key={i}><span>✓</span> {feature}</FeatureItem>
              ))}
            </FeaturesList>
            <Price>
              {typeof pkg.price === 'number' ? pkg.price.toFixed(2) : pkg.price} <PriceSpan>EGP / شهر</PriceSpan>
            </Price>
            {pkg.maxProjects || pkg.max_projects ? <PriceNote>📌 مناسب لـ {pkg.maxProjects || pkg.max_projects} مشروع</PriceNote> : null}
            <SubscribeButton 
              onClick={() => handleSubscribe(pkg)}
            >
              {translations.buttonText[lang]}
            </SubscribeButton>
          </PackageCard>
        ))}
      </PackagesGrid>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <ExtraFeaturesBox>
          <ExtraFeaturesTitle>{translations.extraFeaturesTitle[lang]}</ExtraFeaturesTitle>
          <FeaturesTags>
            {translations.extraTags[lang].map((tag, i) => <FeatureTag key={i}>{tag}</FeatureTag>)}
          </FeaturesTags>
        </ExtraFeaturesBox>
      </div>
    </PackagesSection>
  );
};

const CTASection = ({ lang }) => (
  <CTAContainer>
    <CTAText>{translations.ctaText[lang]}</CTAText>
    <CTAButton to="/contact">{translations.ctaButton[lang]}</CTAButton>
  </CTAContainer>
);

const PageFooter = ({ lang }) => (
  <FooterContainer>
    <FooterLinks>{translations.footerLinks[lang].map((link, i) => <FooterLink key={i}>{link}</FooterLink>)}</FooterLinks>
    <p>{translations.footerText[lang]}</p>
  </FooterContainer>
);

const FloatingRobotWhatsApp = ({ lang }) => (
  <RobotButton href="https://wa.me/201037334294?text=السلام%20عليكم%20أحتاج%20استشارة%20حول%20البوتات%20الذكية" target="_blank" rel="noopener noreferrer">
    <RobotIcon src={whatsappBotImage} alt="WhatsApp Bot" />
    <RobotTooltip>{translations.robotTooltip[lang]}</RobotTooltip>
  </RobotButton>
);

// ==================== Main Component ====================
const Packages = () => {
  const { language: lang, toggleLanguage: toggleLang } = React.useContext(LanguageContext);
  const [dbPackages, setDbPackages] = useState([]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const url = window.location.hostname === 'localhost' ? 'http://localhost/api/manage_packages.php' : '/api/manage_packages.php';
        const res = await axios.get(url);
        if (res.data.status === 'success' && res.data.packages.length > 0) {
          setDbPackages(res.data.packages);
        } else {
          setDbPackages(staticPackages);
        }
      } catch (err) {
        setDbPackages(staticPackages);
      }
    };
    fetchPackages();
  }, []);

  return (
    <PageContainer dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* LangSwitcher Removed */}
      <Container>
        <HeroSectionComponent lang={lang} />
        <PackagesSectionComponent lang={lang} packages={dbPackages} />
        <CTASection lang={lang} />
        <PageFooter lang={lang} />
      </Container>
      <FloatingRobotWhatsApp lang={lang} />
    </PageContainer>
  );
};

export default Packages;